import { IActivity } from './activity'
import { IBorrowingBase, IOngoingReporting } from './bbc'
import { IClientInfo } from './client'
import { ICheck, IWireData } from './collection'
import { IDueDiligenceDocumentRequest, IDueDiligenceDocumentRequestComment } from './dueDiligence'
import { IAdjustment, IFee } from './loanServicing'
import { INewsfeed } from './newsfeed'
import { INote } from './notes'
import { IPassThrough } from './passThrough'
import { ITermLoan } from './termLoan'
import { IUser, NotificationSettingType, UserRole } from './user'

/**
 * Lifecycle of a row in the `scheduled_notifications` queue. Rows start PENDING,
 * and the ProcessScheduledNotifications cron flips them to SENT (delivered) or
 * FAILED (delivery threw). There is no CANCELLED state — voiding a pending row
 * hard-deletes it (see DeleteReminder), so there is nothing to retain.
 */
export enum ScheduledNotificationStatus {
  PENDING = 'pending',
  SENT = 'sent',
  FAILED = 'failed',
}

/**
 * Channels a notification can be delivered through. Read from `data.deliveryMethod`
 * (a list of channels) by notificationSender.send for any notification type. When the
 * list is empty or unset, delivery defaults to every channel and each recipient's Arc
 * notification settings decide which channels they actually receive on.
 */
export enum NotificationDeliveryMethod {
  PUSH = 'push',
  EMAIL = 'email',
}

/**
 * A notification queued for future delivery. A row is, in effect, a deferred
 * notificationSender.send call: when `scheduledAt` arrives the cron replays it as
 * send(type, null, targetUserId, { ...data, message, clientName, deliveryMethod }).
 * Arc reminders are rows of type ArcReminder.
 */
export interface IScheduledNotification {
  id: string
  type: NotificationType
  userId: string | null
  targetUserId: string | null
  message: string | null
  data: Record<string, unknown>
  scheduledAt: string
  status: ScheduledNotificationStatus
  // Channels to deliver on. null/empty means "inherit the recipient's Arc notification settings".
  deliveryMethod: NotificationDeliveryMethod[] | null
  clientName: string | null
  sentAt: string | null
  createdAt: string
  updatedAt: string
}

export enum NotificationType {
  RequestFunding = 'RequestFunding',
  RequestFundingClient = 'RequestFundingClient',
  CancelFunding = 'CancelFunding',
  DeleteFunding = 'DeleteFunding',
  SubmitReporting = 'SubmitReporting',
  FeeCreated = 'FeeCreated',
  FeeUpdated = 'FeeUpdated',
  FeeDeleted = 'FeeDeleted',
  FeeCharged = 'FeeCharged',
  FeeChargedMultiple = 'FeeChargedMultiple',
  AdjustmentCreated = 'AdjustmentCreated',
  AdjustmentUpdated = 'AdjustmentUpdated',
  AdjustmentDeleted = 'AdjustmentDeleted',
  PassThroughUpdated = 'PassThroughUpdated',
  PassThroughDeleted = 'PassThroughDeleted',
  CheckDataCreated = 'CheckDataCreated',
  CheckDataUpdated = 'CheckDataUpdated',
  CheckDataDeleted = 'CheckDataDeleted',
  WiresDataCreated = 'WiresDataCreated',
  WiresDataUpdated = 'WiresDataUpdated',
  WiresDataDeleted = 'WiresDataDeleted',
  ActivityAssign = 'ActivityAssign',
  ActivityDelete = 'ActivityDelete',
  ChecksPostingIssue = 'ChecksPostingIssue',
  NoteTagging = 'NoteTagging',
  NoteTaggingOPS = 'NoteTaggingOPS',
  ClientIntakeSubmit = 'ClientIntakeSubmit',
  ProspectReactivated = 'ProspectReactivated',
  AutoReportingConnected = 'AutoReportingConnected',
  AutoReportingDisconnected = 'AutoReportingDisconnected',
  PasswordReset = 'PasswordReset',
  PasswordResetSuccess = 'PasswordResetSuccess',
  UserInvite = 'UserInvite',
  ProspectUserInvite = 'ProspectUserInvite',
  ParticipantUserInvite = 'ParticipantUserInvite',
  /** An existing client login was given access to another company. */
  ClientAccessGranted = 'ClientAccessGranted',
  UserProfileUpdate = 'UserProfileUpdate',
  ManagerNotification = 'ManagerNotification',
  AuthorizedSignerAdded = 'AuthorizedSignerAdded',
  LoanBalanceReport = 'LoanBalanceReport',
  ClientApplicationFileUpload = 'ClientApplicationFileUpload',
  ClientApplicationFilesUploadSummarizing = 'ClientApplicationFilesUploadSummarizing',
  ClientApplicationStepFinished = 'ClientApplicationStepFinished',
  ClientApplicationDocumentComment = 'ClientApplicationDocumentComment',
  ClientApplicationDocumentCommentsSummarizing = 'ClientApplicationDocumentCommentsSummarizing',
  MaturityDateRenewal = 'MaturityDateRenewal',
  NoticePeriodReminder = 'NoticePeriodReminder',
  OveradvanceCreated = 'OveradvanceCreated',
  OveradvanceUpdated = 'OveradvanceUpdated',
  OveradvanceUpdatedPriority = 'OveradvanceUpdatedPriority',
  OveradvanceDeleted = 'OveradvanceDeleted',
  AmendmentCreated = 'AmendmentCreated',
  AmendmentUpdated = 'AmendmentUpdated',
  AmendmentDeleted = 'AmendmentDeleted',
  OPSSync = 'OPSSync',
  CodatPortfolioSync = 'CodatPortfolioSync',
  CodatUWSync = 'CodatUWSync',
  LoanBalanceDiscrepancy = 'LoanBalanceDiscrepancy',
  LoanBalanceDiscrepancyResolved = 'LoanBalanceDiscrepancyResolved',
  BBCSignRequest = 'BBCSignRequest',
  BBCSignReminder = 'BBCSignReminder',
  BBCSignReminderManual = 'BBCSignReminderManual',
  SubmitAllReportingReminder = 'SubmitAllReportingReminder',
  BBCMonthlyReminder = 'BBCMonthlyReminder',
  BBCProcessingSkipped = 'BBCProcessingSkipped',
  BBCHistoricalDataFailed = 'BBCHistoricalDataFailed',
  ServicingAuditIssues = 'ServicingAuditIssues',
  BBCForeignInventoryLocationZeroBalance = 'BBCForeignInventoryLocationZeroBalance',
  TermLoanCreated = 'TermLoanCreated',
  TermLoanUpdated = 'TermLoanUpdated',
  TermLoanDeleted = 'TermLoanDeleted',
  TermLoanActivityCreated = 'TermLoanActivityCreated',
  TermLoanActivityUpdated = 'TermLoanActivityUpdated',
  TermLoanActivityDeleted = 'TermLoanActivityDeleted',
  NoteReply = 'NoteReply',
  DefaultDiligenceNoteReply = 'DefaultDiligenceNoteReply',
  /** Arc logged a diligence call's answers onto a deal's question list. One per call. */
  DiligenceCallAnswers = 'DiligenceCallAnswers',
  // A call's major developments that could not be recorded, because the call was
  // ambiguous between two or more Current clients. Sent to its internal attendees.
  UnmatchedCallMajorDevelopments = 'UnmatchedCallMajorDevelopments',
  LCRNoteReply = 'LCRNoteReply',
  DDDocumentStatusReminder = 'DDDocumentStatusReminder',
  CodatSyncSummarizingPortfolio = 'CodatSyncSummarizingPortfolio',
  CodatSyncSummarizingUW = 'CodatSyncSummarizingUW',
  CodatSyncSummarizingBD = 'CodatSyncSummarizingBD',
  SubmitOPS = 'SubmitOPS',
  OpsWriteupInReview = 'OpsWriteupInReview',
  /** Batched digest of comments left on a Deal Summary, sent to the person it is assigned to. */
  OpsWriteupComments = 'OpsWriteupComments',
  ApproveOPS = 'ApproveOPS',
  CheckUploadReminder = 'CheckUploadReminder',
  BBCSigned = 'BBCSigned',
  MarketNews = 'MarketNews',
  MarketNewsDigest = 'MarketNewsDigest',
  TermLoanTerminating = 'TermLoanTerminating',
  TermLoanTerminatingIndividual = 'TermLoanTerminatingIndividual',
  OPSProcessingComplete = 'OPSProcessingComplete',
  AccountActivityTotalBalanceMismatch = 'AccountActivityTotalBalanceMismatch',
  AccountActivityAblBalanceMismatch = 'AccountActivityAblBalanceMismatch',
  AccountActivityTotalBalanceMismatchResolved = 'AccountActivityTotalBalanceMismatchResolved',
  AccountActivityAblBalanceMismatchResolved = 'AccountActivityAblBalanceMismatchResolved',
  TaskDue = 'TaskDue',
  Tasks = 'Tasks',
  ScheduledItemsBatch = 'ScheduledItemsBatch',
  ScheduledItemDue = 'ScheduledItemDue',
  ScheduledItemApplied = 'ScheduledItemApplied',
  ClientAmendmentApplied = 'ClientAmendmentApplied',
  ClientAmendmentVoided = 'ClientAmendmentVoided',
  TermLoanActivated = 'TermLoanActivated',
  UserCreated = 'UserCreated',
  UserDeleted = 'UserDeleted',
  TaskMention = 'TaskMention',
  TaskAutoAssigned = 'TaskAutoAssigned',
  // A person assigned a task to someone else. Unlike TaskAutoAssigned (a workflow
  // with no actor), this carries the assigner in `userId` and the assignee in
  // `data.targetUserId` — same shape as TaskCollaboratorActivity.
  TaskAssigned = 'TaskAssigned',
  // Sent to a task's collaborators (followers) on new-comment / completion
  // activity — excluding the actor and anyone already notified via TaskMention.
  TaskCollaboratorActivity = 'TaskCollaboratorActivity',
  ArcAutomationComplete = 'ArcAutomationComplete',
  AtlasChatShared = 'AtlasChatShared',
  ArcReminder = 'ArcReminder',
  // An Arc watch ("ping me when X happens") firing. Deliberately has no in-app
  // home: `resumeArcSession` already delivers the follow-up reply into the
  // conversation, so a notification record would duplicate it. This type exists
  // to send the owner a Desktop/Mobile banner behind the "Arc" setting.
  ArcWatchFired = 'ArcWatchFired',
  // Arc's email triage outcomes — one type per bucket rather than one shared
  // type, because NOTIFICATION_TYPE_SETTING is a static map and each bucket
  // answers to a different row on the Notification Settings page.
  ArcEmailDraftPrepared = 'ArcEmailDraftPrepared',
  ArcEmailReportingFiled = 'ArcEmailReportingFiled',
  ArcEmailIntakeFiled = 'ArcEmailIntakeFiled',
  ArcEmailLeadReferral = 'ArcEmailLeadReferral',
  PlaidConnected = 'PlaidConnected',
  // Error Center: a new or reopened error fingerprint, or an explicit failure alert.
  SystemError = 'SystemError',
  // Team messaging. The audience is named explicitly (`data.targetUserIds`), because who gets
  // notified depends on each person's own channel_member row — notification level, mute window
  // and live presence — not on a role group. `notifyChannelMessage` resolves it, then groups
  // recipients whose notification would read identically so a broadcast writes one row rather
  // than one per member.
  ChannelMention = 'ChannelMention',
  ChannelMessage = 'ChannelMessage',
  // A message the recipient scheduled could not be posted at its send time — they lost
  // access, the conversation was archived, and so on. Only ever sent to that sender.
  ScheduledChannelMessageFailed = 'ScheduledChannelMessageFailed',
  // QuickBooks direct-integration events (notify the portfolio team).
  QboConnectionMade = 'QboConnectionMade',
  QboConnectionExpired = 'QboConnectionExpired',
  QboSyncFailed = 'QboSyncFailed',
  // A client user pushed a manual sync themselves. QboSyncFailed covers a run that
  // left work behind; this one is the FYI that a client reached into their own
  // books, which the portfolio team otherwise never sees.
  QboManualSyncRun = 'QboManualSyncRun',
}

export const NOTIFICATION_TYPE_SETTING: Record<NotificationType, NotificationSettingType> = {
  [NotificationType.RequestFunding]: NotificationSettingType.Funding,
  [NotificationType.CancelFunding]: NotificationSettingType.Funding,
  [NotificationType.DeleteFunding]: NotificationSettingType.Funding,
  [NotificationType.SubmitReporting]: NotificationSettingType.SubmitReporting,
  [NotificationType.FeeCreated]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.FeeUpdated]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.FeeDeleted]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.FeeCharged]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.FeeChargedMultiple]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.AdjustmentCreated]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.AdjustmentUpdated]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.AdjustmentDeleted]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.LoanBalanceDiscrepancy]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.LoanBalanceDiscrepancyResolved]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.AccountActivityTotalBalanceMismatch]:
    NotificationSettingType.FeesAndAdjustments,
  [NotificationType.AccountActivityAblBalanceMismatch]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.AccountActivityTotalBalanceMismatchResolved]:
    NotificationSettingType.FeesAndAdjustments,
  [NotificationType.AccountActivityAblBalanceMismatchResolved]:
    NotificationSettingType.FeesAndAdjustments,
  [NotificationType.TermLoanActivityCreated]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.TermLoanActivityUpdated]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.TermLoanActivityDeleted]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.TermLoanTerminating]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.TermLoanTerminatingIndividual]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.PassThroughUpdated]: NotificationSettingType.PassThrough,
  [NotificationType.PassThroughDeleted]: NotificationSettingType.PassThrough,
  [NotificationType.CheckDataCreated]: NotificationSettingType.Collections,
  [NotificationType.CheckDataUpdated]: NotificationSettingType.Collections,
  [NotificationType.CheckDataDeleted]: NotificationSettingType.Collections,
  [NotificationType.WiresDataCreated]: NotificationSettingType.Collections,
  [NotificationType.WiresDataUpdated]: NotificationSettingType.Collections,
  [NotificationType.WiresDataDeleted]: NotificationSettingType.Collections,
  [NotificationType.ActivityAssign]: NotificationSettingType.Activities,
  [NotificationType.ActivityDelete]: NotificationSettingType.Activities,
  [NotificationType.ChecksPostingIssue]: NotificationSettingType.Activities,
  [NotificationType.NoteTagging]: NotificationSettingType.Notes,
  [NotificationType.NoteTaggingOPS]: NotificationSettingType.Notes,
  [NotificationType.ClientIntakeSubmit]: NotificationSettingType.ClientIntake,
  [NotificationType.ProspectReactivated]: NotificationSettingType.ClientIntake,
  [NotificationType.AutoReportingConnected]: NotificationSettingType.AutoReportingConnected,
  [NotificationType.AutoReportingDisconnected]: NotificationSettingType.AutoReportingConnected,
  [NotificationType.ClientApplicationFileUpload]: NotificationSettingType.ClientApplication,
  [NotificationType.ClientApplicationFilesUploadSummarizing]:
    NotificationSettingType.ClientApplication,
  [NotificationType.ClientApplicationStepFinished]: NotificationSettingType.ClientApplication,
  [NotificationType.ClientApplicationDocumentComment]: NotificationSettingType.ClientApplication,
  [NotificationType.ClientApplicationDocumentCommentsSummarizing]:
    NotificationSettingType.ClientApplication,
  [NotificationType.DDDocumentStatusReminder]: NotificationSettingType.ClientApplication,
  [NotificationType.MaturityDateRenewal]: NotificationSettingType.ClientSettings,
  [NotificationType.NoticePeriodReminder]: NotificationSettingType.ClientSettings,
  [NotificationType.OveradvanceCreated]: NotificationSettingType.ClientSettings,
  [NotificationType.OveradvanceUpdated]: NotificationSettingType.ClientSettings,
  [NotificationType.OveradvanceUpdatedPriority]: NotificationSettingType.ClientSettings,
  [NotificationType.OveradvanceDeleted]: NotificationSettingType.ClientSettings,
  [NotificationType.AmendmentCreated]: NotificationSettingType.ClientSettings,
  [NotificationType.AmendmentUpdated]: NotificationSettingType.ClientSettings,
  [NotificationType.AmendmentDeleted]: NotificationSettingType.ClientSettings,
  [NotificationType.TermLoanCreated]: NotificationSettingType.ClientSettings,
  [NotificationType.TermLoanUpdated]: NotificationSettingType.ClientSettings,
  [NotificationType.TermLoanDeleted]: NotificationSettingType.ClientSettings,
  [NotificationType.OPSSync]: NotificationSettingType.ClientIntake,
  [NotificationType.CodatPortfolioSync]: NotificationSettingType.SubmitReporting,
  [NotificationType.CodatUWSync]: NotificationSettingType.ClientApplication,
  [NotificationType.BBCProcessingSkipped]: NotificationSettingType.Funding,
  [NotificationType.BBCHistoricalDataFailed]: NotificationSettingType.Funding,
  [NotificationType.ServicingAuditIssues]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.BBCForeignInventoryLocationZeroBalance]: NotificationSettingType.Funding,
  [NotificationType.NoteReply]: NotificationSettingType.Notes,
  [NotificationType.DefaultDiligenceNoteReply]: NotificationSettingType.Notes,
  [NotificationType.DiligenceCallAnswers]: NotificationSettingType.Notes,
  [NotificationType.UnmatchedCallMajorDevelopments]: NotificationSettingType.Notes,
  [NotificationType.LCRNoteReply]: NotificationSettingType.Notes,
  [NotificationType.CodatSyncSummarizingPortfolio]: NotificationSettingType.SubmitReporting,
  [NotificationType.CodatSyncSummarizingUW]: NotificationSettingType.ClientApplication,
  [NotificationType.CodatSyncSummarizingBD]: NotificationSettingType.ClientIntake,
  [NotificationType.SubmitOPS]: NotificationSettingType.ClientIntake,
  [NotificationType.OpsWriteupInReview]: NotificationSettingType.ClientIntake,
  [NotificationType.OpsWriteupComments]: NotificationSettingType.Notes,
  [NotificationType.ApproveOPS]: NotificationSettingType.ClientIntake,
  [NotificationType.OPSProcessingComplete]: NotificationSettingType.ClientIntake,
  [NotificationType.CheckUploadReminder]: NotificationSettingType.Activities,
  [NotificationType.BBCSigned]: NotificationSettingType.Funding,
  [NotificationType.MarketNews]: NotificationSettingType.MarketNews,
  [NotificationType.MarketNewsDigest]: NotificationSettingType.MarketNews,

  //system notifications
  [NotificationType.RequestFundingClient]: NotificationSettingType.System,
  [NotificationType.PasswordReset]: NotificationSettingType.System,
  [NotificationType.PasswordResetSuccess]: NotificationSettingType.System,
  [NotificationType.UserInvite]: NotificationSettingType.System,
  [NotificationType.ProspectUserInvite]: NotificationSettingType.System,
  [NotificationType.ParticipantUserInvite]: NotificationSettingType.System,
  [NotificationType.ClientAccessGranted]: NotificationSettingType.System,
  [NotificationType.UserProfileUpdate]: NotificationSettingType.System,
  [NotificationType.ManagerNotification]: NotificationSettingType.System,
  [NotificationType.AuthorizedSignerAdded]: NotificationSettingType.ClientSettings,
  [NotificationType.LoanBalanceReport]: NotificationSettingType.System,
  [NotificationType.BBCSignRequest]: NotificationSettingType.System,
  [NotificationType.BBCSignReminder]: NotificationSettingType.System,
  [NotificationType.BBCSignReminderManual]: NotificationSettingType.System,
  [NotificationType.SubmitAllReportingReminder]: NotificationSettingType.System,
  [NotificationType.BBCMonthlyReminder]: NotificationSettingType.System,
  [NotificationType.TaskDue]: NotificationSettingType.Tasks,
  [NotificationType.Tasks]: NotificationSettingType.Tasks,
  [NotificationType.ScheduledItemsBatch]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.ScheduledItemDue]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.ScheduledItemApplied]: NotificationSettingType.FeesAndAdjustments,
  [NotificationType.ClientAmendmentApplied]: NotificationSettingType.ClientSettings,
  [NotificationType.ClientAmendmentVoided]: NotificationSettingType.ClientSettings,
  [NotificationType.TermLoanActivated]: NotificationSettingType.ClientSettings,
  [NotificationType.UserCreated]: NotificationSettingType.Users,
  [NotificationType.UserDeleted]: NotificationSettingType.Users,
  [NotificationType.TaskMention]: NotificationSettingType.Tasks,
  [NotificationType.TaskAutoAssigned]: NotificationSettingType.Tasks,
  [NotificationType.TaskAssigned]: NotificationSettingType.Tasks,
  [NotificationType.TaskCollaboratorActivity]: NotificationSettingType.Tasks,
  [NotificationType.ArcAutomationComplete]: NotificationSettingType.ArcAutomations,
  // Both Arc-related flows (automation completions + chat share invitations)
  // are gated by the single "Arc" toggle (NotificationSettingType.ArcAutomations,
  // labeled "Arc" in the UI).
  [NotificationType.AtlasChatShared]: NotificationSettingType.ArcAutomations,
  [NotificationType.ArcReminder]: NotificationSettingType.ArcAutomations,
  // A fired watch is an automation result, so it answers to the same "Arc" row
  // as a completed automation run.
  [NotificationType.ArcWatchFired]: NotificationSettingType.ArcAutomations,
  // Each email-triage bucket answers to the page row that owns its subject
  // matter, not to a single "Arc email" row: a filed report is reporting, a
  // filed intake doc and a Salesforce lead are both client intake, and only the
  // reply drafts get a row of their own.
  [NotificationType.ArcEmailDraftPrepared]: NotificationSettingType.ArcEmailDrafts,
  [NotificationType.ArcEmailReportingFiled]: NotificationSettingType.SubmitReporting,
  [NotificationType.ArcEmailIntakeFiled]: NotificationSettingType.ClientIntake,
  [NotificationType.ArcEmailLeadReferral]: NotificationSettingType.ClientIntake,
  [NotificationType.PlaidConnected]: NotificationSettingType.AutoReportingConnected,
  [NotificationType.SystemError]: NotificationSettingType.Errors,
  [NotificationType.ChannelMention]: NotificationSettingType.Messages,
  [NotificationType.ChannelMessage]: NotificationSettingType.Messages,
  [NotificationType.ScheduledChannelMessageFailed]: NotificationSettingType.Messages,
  [NotificationType.QboConnectionMade]: NotificationSettingType.AutoReportingConnected,
  [NotificationType.QboConnectionExpired]: NotificationSettingType.AutoReportingConnected,
  [NotificationType.QboSyncFailed]: NotificationSettingType.AutoReportingConnected,
  [NotificationType.QboManualSyncRun]: NotificationSettingType.AutoReportingConnected,
}

/**
 * Notification categories a role may be shown *at all*.
 *
 * Distinct from `USER_ROLE_NOTIFICATION_SETTINGS` (common/interfaces/user), which
 * decides which preference checkboxes a role is offered: this is the
 * authorization scope applied to the feed itself, and a role listed here cannot
 * widen it by asking for another home or clearing a filter. Absent from this map
 * means unrestricted, which is every role but one.
 *
 * The tasks-and-messaging role is restricted because `notifications/List` is a
 * single query across every type, joining client, borrowing-base, wire, fee,
 * adjustment, note, term-loan and market-news rows — so granting it the service
 * would otherwise hand over the whole portfolio's activity, including rows that
 * predate a user being moved to this role. `System` is in scope because that is
 * where invites, password resets and profile changes live: account-level events
 * about themselves.
 */
export const ROLE_NOTIFICATION_SETTING_SCOPE: Partial<Record<UserRole, NotificationSettingType[]>> =
  {
    [UserRole.TASKS_AND_MESSAGING]: [
      NotificationSettingType.Messages,
      NotificationSettingType.Tasks,
      NotificationSettingType.System,
    ],
  }

/**
 * The notification types a role may be shown, or `null` when it is unrestricted.
 *
 * Derived from `NOTIFICATION_TYPE_SETTING` rather than listed by hand, so a new
 * notification type is scoped by its category the day it is added — the failure
 * mode of a hand-maintained list here is a leak, not a missing row.
 */
export const allowedNotificationTypesForRole = (
  role?: string | null,
): NotificationType[] | null => {
  const scope = ROLE_NOTIFICATION_SETTING_SCOPE[role as UserRole]
  if (!scope) return null

  return (Object.values(NotificationType) as NotificationType[]).filter((type) =>
    scope.includes(NOTIFICATION_TYPE_SETTING[type]),
  )
}

/**
 * Where a notification lands for its recipient (notifications redesign — three homes):
 *  - ACTION_ITEMS: things you need to *do*, cleared by resolving the underlying work
 *    (funding you process, an unresolved alert on a client you cover, a signature request).
 *  - INBOX: things to *read*, cleared by reading. The bell badge counts unread INBOX rows.
 *  - FEED:  everything else that happened. A permanent, browsable log — never badged.
 *  - NONE:  no in-app record at all (email/system-only types the sender can't render in-app,
 *           and the retired TaskDue / DDDocumentStatusReminder nags).
 *
 * A type only earns a non-NONE home if `notificationSender.buildNotification` can render it
 * in-app — otherwise creating the record would throw and break that type's other channels.
 * Several types the product spec routes to Inbox/Action Items (user invites, password resets,
 * standing BBC/reporting reminders, delivered reports) have no in-app renderer yet, so they
 * stay NONE (email-only) until one is added.
 *
 * Home is decided per-(user, event): the workflow/alert types in ACTION_ITEMS marked below are
 * ACTION_ITEMS for the owner/coverer and FEED for watchers; resolveHome()
 * (common/helpers/notificationHome.ts) applies that per-recipient override on top of this map.
 */
export enum NotificationHome {
  ACTION_ITEMS = 'action_items',
  INBOX = 'inbox',
  MESSAGES = 'messages',
  FEED = 'feed',
  NONE = 'none',
}

// Things you need to *do* — cleared by resolving the underlying work, not by reading.
// Only types notificationSender can render in-app belong here; standing email-only reminders
// (BBCSignReminder, BBCMonthlyReminder, SubmitAllReportingReminder, ScheduledItemsBatch) have no
// in-app renderer and stay NONE.
const ACTION_ITEMS_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.RequestFunding, // resolveHome: ACTION_ITEMS for the owner/processor, FEED for watchers
  NotificationType.ActivityAssign,
  NotificationType.ChecksPostingIssue,
  NotificationType.LoanBalanceDiscrepancy,
  NotificationType.AccountActivityTotalBalanceMismatch, // resolveHome: ACTION_ITEMS for covering admins, FEED otherwise
  NotificationType.AccountActivityAblBalanceMismatch,
  NotificationType.BBCForeignInventoryLocationZeroBalance,
  NotificationType.SubmitOPS,
  NotificationType.ArcReminder,
  NotificationType.TaskAutoAssigned,
  // Work someone handed you: cleared by doing the task, not by reading the row —
  // so it sits beside TaskAutoAssigned rather than in the Inbox.
  NotificationType.TaskAssigned,
  NotificationType.BBCSignRequest,
  NotificationType.CheckUploadReminder,
  NotificationType.NoticePeriodReminder,
  NotificationType.TermLoanTerminating,
  NotificationType.TermLoanTerminatingIndividual,
  NotificationType.ScheduledItemDue,
]

// Addressed to you → read it, then it clears. The bell badge counts unread INBOX rows.
const INBOX_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.NoteTagging,
  NotificationType.NoteReply,
  NotificationType.OpsWriteupComments,
  NotificationType.DefaultDiligenceNoteReply,
  NotificationType.DiligenceCallAnswers,
  NotificationType.UnmatchedCallMajorDevelopments,
  NotificationType.LCRNoteReply,
  NotificationType.TaskMention,
  // A comment/completion on a task you're assigned to or have previously commented
  // on — addressed to you, read it, it clears, same as an explicit @-mention.
  NotificationType.TaskCollaboratorActivity,
  NotificationType.AtlasChatShared,
  NotificationType.ArcAutomationComplete, // failures; resolveHome demotes successes to FEED
  // Arc acted on an email addressed to you — a draft to review, a filing to
  // spot-check. Read-to-clear.
  NotificationType.ArcEmailDraftPrepared,
  NotificationType.ArcEmailReportingFiled,
  NotificationType.ArcEmailIntakeFiled,
  NotificationType.ArcEmailLeadReferral,
  NotificationType.SystemError,
  NotificationType.BBCHistoricalDataFailed,
  NotificationType.ServicingAuditIssues,
  NotificationType.ApproveOPS,
  NotificationType.ClientApplicationDocumentComment,
  NotificationType.AuthorizedSignerAdded,
  NotificationType.OPSProcessingComplete,
  NotificationType.BBCSigned,
  NotificationType.ClientApplicationStepFinished,
  NotificationType.ClientIntakeSubmit,
  NotificationType.ProspectReactivated,
  NotificationType.BBCProcessingSkipped,
  NotificationType.AutoReportingDisconnected,
  NotificationType.MaturityDateRenewal, // moved from Action Items to Inbox per PM directive
  // QBO sync problems mirror their existing analogues: an expired connection is the
  // QBO twin of AutoReportingDisconnected, and a failed nightly sync is a processing
  // failure like BBCHistoricalDataFailed. Both need someone to look.
  NotificationType.QboConnectionExpired,
  NotificationType.QboSyncFailed,
  // Addressed to one person about their own message, and it needs them to act — not
  // conversation traffic, so it sits in the Inbox rather than the Messages home.
  NotificationType.ScheduledChannelMessageFailed,
]

// Team messaging, in a home of its own rather than mixed into the Inbox.
// Conversation moves at a different rate from everything else the Inbox carries:
// a busy morning in one channel would bury the funding request and reporting
// notices it sits next to, and the two are read in different modes. Volume is
// already gated upstream on the `channel_member` row (notification level, mute
// window, presence) before send() is called — this is about where the surviving
// ones land, not how many there are.
const MESSAGES_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.ChannelMention,
  NotificationType.ChannelMessage,
]

// Operational milestones / FYI that create an in-app record → browsable, never badged.
const FEED_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.CancelFunding,
  NotificationType.DeleteFunding,
  // Activity/Feed per PM directive — a reporting submission is an FYI to watchers, and there's no
  // reviewer assignee at submit time to single out for the Inbox.
  NotificationType.SubmitReporting,
  NotificationType.FeeCreated,
  NotificationType.FeeUpdated,
  NotificationType.FeeDeleted,
  NotificationType.FeeCharged,
  NotificationType.AdjustmentCreated,
  NotificationType.AdjustmentUpdated,
  NotificationType.AdjustmentDeleted,
  NotificationType.PassThroughUpdated,
  NotificationType.PassThroughDeleted,
  NotificationType.CheckDataCreated,
  NotificationType.CheckDataUpdated,
  NotificationType.CheckDataDeleted,
  NotificationType.WiresDataCreated,
  NotificationType.WiresDataUpdated,
  NotificationType.WiresDataDeleted,
  NotificationType.ActivityDelete,
  NotificationType.AutoReportingConnected,
  NotificationType.ClientApplicationFileUpload,
  NotificationType.OveradvanceCreated,
  NotificationType.OveradvanceUpdated,
  NotificationType.OveradvanceUpdatedPriority,
  NotificationType.OveradvanceDeleted,
  NotificationType.AmendmentCreated,
  NotificationType.AmendmentUpdated,
  NotificationType.AmendmentDeleted,
  NotificationType.TermLoanCreated,
  NotificationType.TermLoanUpdated,
  NotificationType.TermLoanDeleted,
  NotificationType.OPSSync,
  NotificationType.CodatPortfolioSync,
  NotificationType.CodatUWSync,
  NotificationType.LoanBalanceDiscrepancyResolved,
  NotificationType.AccountActivityTotalBalanceMismatchResolved,
  NotificationType.AccountActivityAblBalanceMismatchResolved,
  NotificationType.TermLoanActivityCreated,
  NotificationType.TermLoanActivityUpdated,
  NotificationType.TermLoanActivityDeleted,
  NotificationType.MarketNews,
  NotificationType.ScheduledItemApplied,
  NotificationType.ClientAmendmentApplied,
  NotificationType.ClientAmendmentVoided,
  NotificationType.TermLoanActivated,
  NotificationType.UserCreated,
  NotificationType.UserDeleted,
  NotificationType.PlaidConnected,
  // A new QBO connection is an FYI milestone, exactly like PlaidConnected and
  // AutoReportingConnected above — informative, not something to action.
  NotificationType.QboConnectionMade,
  // Likewise a client-run manual sync: worth a record that it happened, but the
  // run itself needs no follow-up (QboSyncFailed badges it if it left work behind).
  NotificationType.QboManualSyncRun,
]

export const NOTIFICATION_HOME: Record<NotificationType, NotificationHome> = Object.fromEntries(
  (Object.values(NotificationType) as NotificationType[]).map((type) => [
    type,
    ACTION_ITEMS_NOTIFICATION_TYPES.includes(type)
      ? NotificationHome.ACTION_ITEMS
      : INBOX_NOTIFICATION_TYPES.includes(type)
        ? NotificationHome.INBOX
        : MESSAGES_NOTIFICATION_TYPES.includes(type)
          ? NotificationHome.MESSAGES
          : FEED_NOTIFICATION_TYPES.includes(type)
            ? NotificationHome.FEED
            : NotificationHome.NONE,
  ]),
) as Record<NotificationType, NotificationHome>

export const EMAIL_NOTIFICATIONS = [
  NotificationType.SystemError,
  NotificationType.RequestFundingClient,
  NotificationType.RequestFunding,
  NotificationType.CancelFunding,
  NotificationType.DeleteFunding,
  NotificationType.PasswordReset,
  NotificationType.PasswordResetSuccess,
  NotificationType.UserInvite,
  NotificationType.ProspectUserInvite,
  NotificationType.ParticipantUserInvite,
  NotificationType.ClientAccessGranted,
  NotificationType.UserProfileUpdate,
  NotificationType.ManagerNotification,
  NotificationType.AuthorizedSignerAdded,
  NotificationType.FeeCreated,
  NotificationType.FeeUpdated,
  NotificationType.FeeDeleted,
  NotificationType.FeeChargedMultiple,
  NotificationType.AdjustmentCreated,
  NotificationType.AdjustmentUpdated,
  NotificationType.AdjustmentDeleted,
  NotificationType.PassThroughUpdated,
  NotificationType.PassThroughDeleted,
  NotificationType.CheckDataCreated,
  NotificationType.CheckDataUpdated,
  NotificationType.CheckDataDeleted,
  NotificationType.WiresDataCreated,
  NotificationType.WiresDataUpdated,
  NotificationType.WiresDataDeleted,
  NotificationType.ActivityAssign,
  NotificationType.ActivityDelete,
  NotificationType.LoanBalanceReport,
  NotificationType.NoteTagging,
  NotificationType.NoteTaggingOPS,
  NotificationType.ClientIntakeSubmit,
  NotificationType.ProspectReactivated,
  NotificationType.AutoReportingConnected,
  NotificationType.AutoReportingDisconnected,
  NotificationType.ClientApplicationFilesUploadSummarizing,
  NotificationType.ClientApplicationStepFinished,
  NotificationType.ClientApplicationDocumentCommentsSummarizing,
  NotificationType.MaturityDateRenewal,
  NotificationType.NoticePeriodReminder,
  NotificationType.OveradvanceCreated,
  NotificationType.OveradvanceUpdated,
  NotificationType.OveradvanceUpdatedPriority,
  NotificationType.OveradvanceDeleted,
  NotificationType.AmendmentCreated,
  NotificationType.AmendmentUpdated,
  NotificationType.AmendmentDeleted,
  NotificationType.TermLoanCreated,
  NotificationType.TermLoanUpdated,
  NotificationType.TermLoanDeleted,
  NotificationType.OPSSync,
  NotificationType.LoanBalanceDiscrepancy,
  NotificationType.LoanBalanceDiscrepancyResolved,
  NotificationType.AccountActivityTotalBalanceMismatch,
  NotificationType.AccountActivityAblBalanceMismatch,
  NotificationType.AccountActivityTotalBalanceMismatchResolved,
  NotificationType.AccountActivityAblBalanceMismatchResolved,
  NotificationType.BBCSignRequest,
  NotificationType.BBCSignReminder,
  NotificationType.BBCSignReminderManual,
  NotificationType.SubmitAllReportingReminder,
  NotificationType.BBCMonthlyReminder,
  NotificationType.ChecksPostingIssue,
  NotificationType.TermLoanActivityCreated,
  NotificationType.TermLoanActivityUpdated,
  NotificationType.TermLoanActivityDeleted,
  NotificationType.NoteReply,
  NotificationType.OpsWriteupComments,
  NotificationType.DefaultDiligenceNoteReply,
  NotificationType.LCRNoteReply,
  NotificationType.DDDocumentStatusReminder,
  NotificationType.CodatSyncSummarizingPortfolio,
  NotificationType.CodatSyncSummarizingUW,
  NotificationType.CodatSyncSummarizingBD,
  NotificationType.SubmitOPS,
  NotificationType.OpsWriteupInReview,
  NotificationType.ApproveOPS,
  NotificationType.CheckUploadReminder,
  NotificationType.BBCSigned,
  NotificationType.BBCHistoricalDataFailed,
  NotificationType.ServicingAuditIssues,
  NotificationType.MarketNewsDigest,
  NotificationType.TermLoanTerminating,
  NotificationType.OPSProcessingComplete,
  NotificationType.Tasks,
  NotificationType.ScheduledItemsBatch,
  NotificationType.ClientAmendmentApplied,
  NotificationType.ClientAmendmentVoided,
  NotificationType.TermLoanActivated,
  NotificationType.UserCreated,
  NotificationType.UserDeleted,
  NotificationType.TaskAutoAssigned,
  NotificationType.TaskAssigned,
  NotificationType.TaskMention,
  NotificationType.ArcAutomationComplete,
  NotificationType.AtlasChatShared,
  NotificationType.ArcReminder,
  NotificationType.PlaidConnected,
]

// Browser push defaults to the "needs you" homes only — directly-addressed / time-sensitive
// (Action Items + Inbox + Messages). Feed FYIs (fees, syncs, CRUD, milestones) never push by
// default. Push remains opt-in per user on top of this eligibility set: notificationSender only
// pushes when the caller requests it AND the recipient has push enabled for the type's category.
//
// Messages are listed explicitly rather than inherited: they were split out of the Inbox so a
// busy channel could not bury a funding request (see MESSAGES_NOTIFICATION_TYPES), and that split
// silently took them out of this list too — which meant `notificationSender` never queued a push
// for a channel message no matter what the recipient had toggled on. Volume is already gated
// upstream on the `channel_member` row before send() is called, so eligibility here is safe.
export const PUSH_NOTIFICATIONS: NotificationType[] = [
  ...ACTION_ITEMS_NOTIFICATION_TYPES,
  ...INBOX_NOTIFICATION_TYPES,
  ...MESSAGES_NOTIFICATION_TYPES,
  // Banner-only: Arc already posts the reply in the conversation, so no Inbox row.
  NotificationType.ArcWatchFired,
]

/**
 * Channel messages are deliberately NOT in EMAIL_NOTIFICATIONS.
 *
 * The sender has no batching, digest or quiet-hours mechanism, so a chatty
 * channel would emit one email per message. Volume gating for messaging lives
 * on the `channel_member` row (notification level, mute window, presence) and is
 * applied before send() is ever called — see `services/channels/notifyChannelMessage.ts`.
 */

export const NOTIFICATION_TYPE_ACTION_MAP = {
  [NotificationType.FeeCreated]: 'added',
  [NotificationType.FeeUpdated]: 'updated',
  [NotificationType.FeeDeleted]: 'deleted',
  [NotificationType.AdjustmentCreated]: 'added',
  [NotificationType.AdjustmentUpdated]: 'updated',
  [NotificationType.AdjustmentDeleted]: 'deleted',
  [NotificationType.PassThroughUpdated]: 'updated',
  [NotificationType.PassThroughDeleted]: 'deleted',
  [NotificationType.CheckDataCreated]: 'added',
  [NotificationType.CheckDataUpdated]: 'updated',
  [NotificationType.CheckDataDeleted]: 'deleted',
  [NotificationType.WiresDataCreated]: 'added',
  [NotificationType.WiresDataUpdated]: 'updated',
  [NotificationType.WiresDataDeleted]: 'deleted',
  [NotificationType.OveradvanceCreated]: 'added',
  [NotificationType.OveradvanceUpdated]: 'updated',
  [NotificationType.OveradvanceDeleted]: 'deleted',
  [NotificationType.AmendmentCreated]: 'added',
  [NotificationType.AmendmentUpdated]: 'updated',
  [NotificationType.AmendmentDeleted]: 'deleted',
  [NotificationType.TermLoanCreated]: 'added',
  [NotificationType.TermLoanUpdated]: 'updated',
  [NotificationType.TermLoanDeleted]: 'deleted',
  [NotificationType.TermLoanActivityCreated]: 'added',
  [NotificationType.TermLoanActivityUpdated]: 'updated',
  [NotificationType.TermLoanActivityDeleted]: 'deleted',
}

export const ACCOUNT_ACTIVITY_NOTIFICATION_TYPES_MAP = {
  [NotificationType.FeeCreated]: 'fee',
  [NotificationType.FeeUpdated]: 'fee',
  [NotificationType.FeeDeleted]: 'fee',
  [NotificationType.AdjustmentCreated]: 'adjustment',
  [NotificationType.AdjustmentUpdated]: 'adjustment',
  [NotificationType.AdjustmentDeleted]: 'adjustment',
  [NotificationType.PassThroughUpdated]: 'pass-through',
  [NotificationType.PassThroughDeleted]: 'pass-through',
  [NotificationType.CheckDataCreated]: 'check',
  [NotificationType.CheckDataUpdated]: 'check',
  [NotificationType.CheckDataDeleted]: 'check',
  [NotificationType.WiresDataCreated]: 'wire',
  [NotificationType.WiresDataUpdated]: 'wire',
  [NotificationType.WiresDataDeleted]: 'wire',
  [NotificationType.TermLoanActivityCreated]: 'on a Term Loan',
  [NotificationType.TermLoanActivityUpdated]: 'on a Term Loan',
  [NotificationType.TermLoanActivityDeleted]: 'on a Term Loan',
}

export interface INotification {
  id: string
  type: NotificationType
  clientName: string | null
  clientInfo: IClientInfo | null
  notificationText: string | null
  activityId: string | null
  activity: IActivity | null
  borrowingBaseId: string | null
  borrowingBase: IBorrowingBase | null
  ongoingReportingId: string | null
  ongoingReporting: IOngoingReporting | null
  feeId: string | null
  fee: IFee | null
  adjustmentId: string | null
  adjustment: IAdjustment | null
  termLoanId: string | null
  termLoan: ITermLoan | null
  wiresDataId: string | null
  wiresData: IWireData | null
  checkDataId: string | null
  checkData: ICheck | null
  passThroughsId: string | null
  passThroughs: IPassThrough | null
  ddDocumentRequestId: string | null
  ddDocumentRequest: IDueDiligenceDocumentRequest | null
  ddDocumentRequestCommentId: string | null
  ddDocumentRequestComment: IDueDiligenceDocumentRequestComment | null
  newsfeedHeadlineId: string | null
  newsfeedHeadline: INewsfeed | null
  noteId: string | null
  note: INote | null
  taskId: string | null
  userId: string | null
  user: IUser | null
  data: any
  isRead?: boolean
  home?: NotificationHome
  createdAt: string
}

/**
 * Whole-account unread counts. Deliberately independent of the list's tab and filters
 * (see getNotificationsData), so every tab's response carries the same three numbers.
 */
export interface INotificationCounts {
  // The bell's count. Always equals actionItemsUnread, so the bell matches the number on the
  // Notifications page's Action Items tab; inboxUnread only drives that page's Inbox dot.
  totalUnread: number
  actionItemsUnread: number
  inboxUnread: number
}

export interface INotificationData extends INotificationCounts {
  data: INotification[]
  total: number
}

/**
 * One file's outcome inside an OPSProcessingComplete summary. A submission produces a
 * single notification carrying one of these per file, rather than one notification per file.
 */
export interface IOpsSubmissionFileSummary {
  fileName: string
  reportTypeLabel: string
  succeeded: boolean
}

/** One bullet of an UnmatchedCallMajorDevelopments notification, in the order it was said. */
export interface IUnmatchedCallDevelopment {
  text: string
  /** Where in the call it was said (`M:SS` / `H:MM:SS`), when the transcript has a start time. */
  timeInCall: string | null
}

export enum EmailNotificationStatus {
  Pending = 0,
  Sent = 1,
  Failed = 2,
}
